import { useFocusNextInputOnEnter } from "@result/rui/hooks";
import React from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route } from "react-router";

import { IsAuthorized } from "#/components/Authorization";
import NotAuthorized from "#/components/NotAuthorized";
import * as LTE from "#/def/lookup-tables.enums.gen";
import { useDisableFormsIfReadOnly } from "#/hooks/useDisableFormsIfReadOnly";
import { useSubmitFormOnKey } from "#/hooks/useSubmitFormOnKey";
import RouterSwitch from "#/layout/RouterSwitch";
import BreedingValuesRoutes from "#/pages/breeding-values/BreedingValuesRoutes";
import ProfileRoutes from "#/pages/profile/Routes";

/**
 * The cattle module is Plemenske vrednosti and nothing else.
 *
 * Access is gated on KIS — the lock cattle breeding values have always used,
 * now that this app is their only home — plus ADMIN, who administer the feature
 * itself (BreedingValuesPage exposes the upload-with-šifranti action to them).
 *
 * Note ADMIN is deliberately not redirected here from the animal apps the way
 * KIS is: administrators work across all modules, so they reach this app by
 * choosing it, not by being sent.
 * `useModuleAccessGuard` is deliberately not used: there is no `dostopGovedo`
 * flag on the backend subjekt, so `hasModuleAccess` would return true for
 * everyone, and registering a cattle module in `moduleAccess.MODULES` would pull
 * this app into the other apps' cross-module redirect logic.
 */
export default function Routes() {
  const { t } = useTranslation();

  useFocusNextInputOnEnter();
  useSubmitFormOnKey();
  useDisableFormsIfReadOnly();

  return (
    <div className="main-content">
      <RouterSwitch>
        <Route
          path="breeding-values/*"
          name={t("breedingValues")}
          element={
            <IsAuthorized
              as={[LTE.SifNivoDostopa.KIS, LTE.SifNivoDostopa.ADMIN]}
              fallback={<NotAuthorized />}>
              <BreedingValuesRoutes />
            </IsAuthorized>
          }
        />
        <Route path="profile/*" element={<ProfileRoutes />} />
        {/* No landing page to fall back to — everything else is the feature. */}
        <Route path="*" element={<Navigate to="/breeding-values" replace />} />
      </RouterSwitch>
    </div>
  );
}
